import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import assistentAPI from '../../api/assistent';

const state = {
  records: [],
  uiFlags: {
    fetchingList: false,
    creatingItem: false,
    deletingItem: false,
    updatingItem: false,
  },
  tempFiles: [],
  tempVectorStoreId: null,
};

export const getters = {
  getAssistents(_state) {
    return _state.records.sort((w1, w2) => w1.id - w2.id);
  },
  getAssistent: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, params = { version: 'v1' }) {
    commit(types.default.SET_ASSISTENT_UI_FLAG, { fetchingList: true });
    try {
      const response = await assistentAPI.getByVersion(params.version);
      commit(types.default.SET_ASSISTENT, response.data.payload.assistents);
      commit(types.default.SET_ASSISTENT_UI_FLAG, { fetchingList: false });
    } catch (error) {
      commit(types.default.SET_ASSISTENT_UI_FLAG, { fetchingList: false });
    }
  },

  async create({ commit, dispatch, state }, params) {
    commit(types.default.SET_ASSISTENT_UI_FLAG, { creatingItem: true });
    try {
      // Handle both FormData and regular object
      const response = await assistentAPI.create(params);
      const {
        payload: { assistent },
      } = response.data;
      commit(types.default.ADD_ASSISTENT, assistent);
      commit(
        'integrations/SET_INTEGRATION_ENABLED',
        { appId: 'assistent', enabled: true },
        { root: true }
      );
      commit(types.default.SET_ASSISTENT_UI_FLAG, { creatingItem: false });

      const tempFiles = state.tempFiles.filter(blobSignedId => {
        return (
          assistent.files &&
          !assistent.files.find(file => file.blob_id === blobSignedId)
        );
      });
      dispatch('setTempFiles', tempFiles);
      dispatch('setTempVectorStoreId', null);
      dispatch('submitDeleteTempFilesAndVectorStore');
    } catch (error) {
      commit(types.default.SET_ASSISTENT_UI_FLAG, { creatingItem: false });
      throw error;
    }
  },

  update: async ({ commit, dispatch, state }, params) => {
    commit(types.default.SET_ASSISTENT_UI_FLAG, { updatingItem: true });
    try {
      let id;
      let updateObj;

      // Handle FormData case
      if (params instanceof FormData) {
        id = params.get('id');
        updateObj = params;
      } else {
        // Handle regular object case
        id = params.id;
        updateObj = { assistent: params.assistent };
      }

      const response = await assistentAPI.update(id, updateObj);
      commit(types.default.UPDATE_ASSISTENT, response.data.payload.assistent);
      const assistent = response.data.payload.assistent;
      const tempFiles = state.tempFiles.filter(blobSignedId => {
        return (
          assistent.files &&
          !assistent.files.find(file => file.blob_id === blobSignedId)
        );
      });
      dispatch('setTempFiles', tempFiles);
      dispatch('setTempVectorStoreId', null);
      dispatch('submitDeleteTempFilesAndVectorStore');
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_ASSISTENT_UI_FLAG, { updatingItem: false });
    }
  },

  async delete({ commit, getters }, id) {
    commit(types.default.SET_ASSISTENT_UI_FLAG, { deletingItem: true });
    try {
      await assistentAPI.delete(id);
      commit(types.default.DELETE_ASSISTENT, id);
      const remaining = getters.getAssistents.filter(r => r.id !== id);
      commit(
        'integrations/SET_INTEGRATION_ENABLED',
        { appId: 'assistent', enabled: remaining.length > 0 },
        { root: true }
      );
      commit(types.default.SET_ASSISTENT_UI_FLAG, { deletingItem: false });
    } catch (error) {
      commit(types.default.SET_ASSISTENT_UI_FLAG, { deletingItem: false });
      throw error;
    }
  },

  setTempFiles({ commit }, blobSignedIds) {
    commit(types.default.SET_ASSISTENT_TEMP_FILES, blobSignedIds);
  },

  setTempVectorStoreId({ commit }, vectorStoreId) {
    commit(types.default.SET_ASSISTENT_TEMP_VECTOR_STORE_ID, vectorStoreId);
  },

  submitDeleteTempFilesAndVectorStore({ commit, state }) {
    const tempFiles = state.tempFiles;
    const tempVectorStoreId = state.tempVectorStoreId;
    commit(types.default.SET_ASSISTENT_TEMP_FILES, []);
    commit(types.default.SET_ASSISTENT_TEMP_VECTOR_STORE_ID, null);

    tempFiles.forEach(blobSignedId => {
      assistentAPI.deleteFile(null, blobSignedId);
    });
    if (tempVectorStoreId) {
      assistentAPI.deleteVectorStore(tempVectorStoreId);
    }
  },
};

export const mutations = {
  [types.default.SET_ASSISTENT_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_ASSISTENT]: MutationHelpers.set,
  [types.default.ADD_ASSISTENT]: MutationHelpers.create,
  [types.default.DELETE_ASSISTENT]: MutationHelpers.destroy,
  [types.default.UPDATE_ASSISTENT]: MutationHelpers.update,
  [types.default.SET_ASSISTENT_TEMP_FILES]: (state, blobSignedIds) => {
    state.tempFiles = blobSignedIds;
  },
  [types.default.SET_ASSISTENT_TEMP_VECTOR_STORE_ID]: (
    state,
    vectorStoreId
  ) => {
    state.tempVectorStoreId = vectorStoreId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
